import React, { useEffect, useRef } from 'react';
import type { Location } from 'history';
import { browserHistory } from '@cotera/client/app/history';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';

type BlockTransaction = { retry: () => void };

export const useBlockHistory = (
  shouldBlock: () => boolean,
  opts?: {
    message?: string;
  }
) => {
  useEffect(() => {
    const unblock = browserHistory.block((tx) => {
      if (shouldBlock()) {
        if (
          window.confirm(
            opts?.message ??
              'Unsaved changes will be lost. Are you sure you want to leave?'
          )
        ) {
          // Unblock and retry the transition if confirmed
          unblock();
          tx.retry();
        }
      } else {
        unblock();
        tx.retry();
      }
      // If the user cancels, do nothing and navigation remains blocked.
    });

    return () => {
      unblock();
    };
  }, [shouldBlock, opts?.message]);
};

const DEFAULT_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave?';

/**
 * When provided, called before showing the block modal. If it returns true,
 * navigation proceeds (e.g. after saving). If false, the modal is shown.
 */
export type OnSaveAndProceed = (location: Location) => Promise<boolean>;

type BlockNavigationModalOpts = {
  message?: string;
  onSaveAndProceed?: OnSaveAndProceed;
  /**
   * When false, `history.block` is not registered at all for this hook.
   * History v5 installs a global `beforeunload` whenever any blocker exists;
   * that handler always prompts and never consults `shouldBlock()`, so keeping
   * a blocker mounted while "saved" still blocks tab close / full navigations.
   * Pass the same reactive flag you use for unsaved UI (e.g. `hasUnsavedChanges`).
   */
  blockEnabled?: boolean;
};

/**
 * Blocks navigation when shouldBlock() returns true, pushing a ModalPageView
 * to the modal manager for confirmation.
 *
 * When onSaveAndProceed is provided and returns true for a destination,
 * saves and allows navigation instead of showing the modal (e.g. when
 * navigating to agent chat).
 */
export const useBlockNavigationWithModal = (
  shouldBlock: () => boolean,
  opts?: BlockNavigationModalOpts
) => {
  const modalManager = useModalManager((s) => s.actions);
  const pendingTxRef = useRef<BlockTransaction | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);
  const shouldBlockRef = useRef(shouldBlock);
  const messageRef = useRef(opts?.message ?? DEFAULT_MESSAGE);
  const onSaveAndProceedRef = useRef(opts?.onSaveAndProceed);
  const blockEnabled = opts?.blockEnabled !== false;
  const blockEnabledRef = useRef(blockEnabled);
  blockEnabledRef.current = blockEnabled;

  shouldBlockRef.current = shouldBlock;
  messageRef.current = opts?.message ?? DEFAULT_MESSAGE;
  onSaveAndProceedRef.current = opts?.onSaveAndProceed;

  useEffect(() => {
    if (!blockEnabled) {
      return;
    }

    const unblock = browserHistory.block((tx) => {
      if (shouldBlockRef.current()) {
        const onSaveAndProceed = onSaveAndProceedRef.current;
        if (onSaveAndProceed !== undefined) {
          void onSaveAndProceed(tx.location).then((proceed) => {
            if (proceed) {
              unblock();
              tx.retry();
            } else if (!shouldBlockRef.current() || !blockEnabledRef.current) {
              // e.g. save finished and cleared unsaved state before this microtask ran,
              // or the blocker was torn down because blockEnabled went false.
              unblock();
              tx.retry();
            } else {
              pendingTxRef.current = tx;
              unblockRef.current = unblock;
              showBlockModal();
            }
          });
        } else {
          pendingTxRef.current = tx;
          unblockRef.current = unblock;
          showBlockModal();
        }
      } else {
        unblock();
        tx.retry();
      }
    });

    function showBlockModal() {
      const handleConfirm = () => {
        const pendingTx = pendingTxRef.current;
        const unblockFn = unblockRef.current;
        pendingTxRef.current = null;
        unblockRef.current = null;
        modalManager.pop();
        if (pendingTx !== null && unblockFn !== null) {
          unblockFn();
          pendingTx.retry();
        }
      };

      const handleCancel = () => {
        pendingTxRef.current = null;
        unblockRef.current = null;
        modalManager.pop();
      };

      modalManager.push({
        view: () => (
          <ModalPageView title="Confirm Navigation" className="p-4">
            <div className="flex flex-col gap-4">
              <Text.P>{messageRef.current}</Text.P>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Stay
                </Button>
                <Button onClick={handleConfirm} variant="destructive">
                  Leave
                </Button>
              </div>
            </div>
          </ModalPageView>
        ),
        onClose: handleCancel,
      });
    }

    return () => {
      unblock();
    };
  }, [blockEnabled, modalManager]);
};
